/* eslint-disable max-len */
import {
    Box,
    Button,
    Flex,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import { ComponentProps } from 'react';
import { PortfolioProperty, PortfolioPropertyTag, PropertyKeyPickupLocation } from '@acp/shared';
import { ThemeTokens } from '@acp/core-ui';
import { useProperty, usePropertyKeyPickupLocation } from '../../hooks/use-property';
import { PropertyAddressCard } from '../property-address-card';
import { IntegratedPropertyDialog } from '../integrated-property-dialog';
import { KeyOnsiteAvailabilityText } from './key-onsite-availability-text';

function AgencyKeyFields({
    isIntegratedProperty,
    propertyData,
    keyPickupLocationData,
}: {
    isIntegratedProperty: boolean;
    propertyData: PortfolioProperty;
    keyPickupLocationData: PropertyKeyPickupLocation;
}) {
    return (
        <VStack align="left" gap={2}>
            {isIntegratedProperty && (
                <Box pb={4}>
                    <IntegratedPropertyDialog
                        title="Key details for this property is integrated with your property management software"
                    />
                </Box>
            )}

            <VStack align="left" pb={4}>
                <Text fontWeight="medium">Property manager</Text>
                <Text>{propertyData.propertyManager?.name ?? 'Unknown'}</Text>
            </VStack>

            <VStack
                align="left"
                gap={6}
                w="full"
                p={6}
                borderColor={ThemeTokens.Defaults.borderColor}
                borderWidth={1}
                borderRadius="md"
            >
                <VStack align="left" w="full">
                    <Text fontWeight="medium">Is key cut & available to collect from the agency?</Text>
                    <Text>{propertyData.isKeyAvailable ? 'Yes' : 'No'}</Text>
                </VStack>

                <VStack align="left" w="full">
                    <Text fontWeight="medium">Key number</Text>
                    <Text>{propertyData.keyNumber ?? 'N/A'}</Text>

                    {propertyData.isKeyAvailable
                        && propertyData.keyNumber !== null
                        && propertyData.keyLocationId === null
                        && (<Text fontSize="sm">This key can be picked up at this agency office</Text>)}
                </VStack>

                {propertyData.isKeyAvailable && (
                    <VStack align="left" w="full">
                        <Text fontWeight="medium">Key pickup location</Text>
                        <Text>{keyPickupLocationData.fullAddress}</Text>
                    </VStack>
                )}
            </VStack>
        </VStack>
    );
}

export function ReadOnlyPropertyScreen({
    propertyId,
    onClose,
    showHeader = true,
    showCloseButton = true,
    p,
    showAgencyKeyFields = true,
    showOnsiteKeyFields = true,
}: {
    propertyId: number;
    onClose: () => void;
    showHeader?: boolean;
    showCloseButton?: boolean;
    p?: ComponentProps<typeof ModalBody>['p'];
    showAgencyKeyFields?: boolean;
    showOnsiteKeyFields?: boolean;
}) {
    const propertyQuery = useProperty(propertyId);
    const propertyPickupLocationQuery = usePropertyKeyPickupLocation(propertyId);
    const tags = propertyQuery.data?.tags ?? [];
    const isIntegratedProperty = tags.includes(PortfolioPropertyTag.Integrated);

    if (
        propertyQuery.data === undefined
        || propertyPickupLocationQuery.data === undefined
        || propertyPickupLocationQuery.isLoading
        || propertyQuery.isLoading
    ) {
        return (
            <ModalBody p={p}>
                <Box w="full">
                    <Flex
                        alignItems="center"
                        fontWeight="bold"
                        justifyContent="center"
                        py={8}
                        px={8}
                        borderColor="muted.50"
                        borderWidth={1}
                        rounded="md"
                    >
                        <Spinner />
                    </Flex>
                </Box>
            </ModalBody>
        );
    }

    const propertyData = propertyQuery.data;
    const keyPickupLocationData = propertyPickupLocationQuery.data;
    const propertyKeyInfo = propertyData.propertyKeyInformation;

    return (
        <VStack align="left" w="full">
            {showHeader && (
                <ModalHeader>Property details</ModalHeader>
            )}

            <ModalBody p={p}>
                {showHeader && (
                    <PropertyAddressCard w="full" mb={2} propertyId={propertyId} />
                )}

                <VStack align="left" gap={4} w="full">
                    {showAgencyKeyFields && (
                        <AgencyKeyFields
                            isIntegratedProperty={isIntegratedProperty}
                            propertyData={propertyData}
                            keyPickupLocationData={keyPickupLocationData}
                        />
                    )}

                    {showOnsiteKeyFields && (
                        <VStack
                            align="left"
                            p={6}
                            borderColor={ThemeTokens.Defaults.borderColor}
                            borderWidth={1}
                            borderRadius="md"
                        >
                            <Text fontWeight="medium">Is a key onsite at the property?</Text>

                            <Box w="fit-content">
                                <KeyOnsiteAvailabilityText
                                    onsiteAvailability={propertyKeyInfo.onsiteAvailability}
                                    availableUntil={propertyKeyInfo.temporaryKeyExpirationDate}
                                    notes={propertyKeyInfo.keyOnsiteNotes}
                                />
                            </Box>
                        </VStack>
                    )}
                </VStack>
            </ModalBody>
            <ModalFooter p={p} justifyContent="space-between">
                {showCloseButton && (
                    <Button
                        size="md"
                        colorScheme="muted"
                        variant="ghost"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                )}
            </ModalFooter>
        </VStack>
    );
}
